import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import {
  APP_SETTINGS_N8N_LISTINGS_FOLDERS_KEY,
  APP_SETTINGS_N8N_PROCESSED_FOLDER_KEY,
  APP_SETTINGS_QUERY_KEY,
  LISTINGS_MANAGEMENT_DASHBOARD_QUERY_KEY,
} from '@/config/appSettingsKeys'
import {
  parseListingsFoldersJson,
  serializeListingsFolders,
  type ListingsFolderEntry,
} from '@/lib/listingsFolderConfig'
import { AdminSectionCard } from '@/components/listings-management/AdminSectionCard'

interface FolderConfigSectionProps {
  initialFoldersJson: unknown
  initialProcessedFolderId: string | null
}

export function FolderConfigSection({ initialFoldersJson, initialProcessedFolderId }: FolderConfigSectionProps) {
  const queryClient = useQueryClient()
  const [folders, setFolders] = useState<ListingsFolderEntry[]>([])
  const [processedFolderId, setProcessedFolderId] = useState('')
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    const raw =
      typeof initialFoldersJson === 'string'
        ? initialFoldersJson
        : initialFoldersJson != null
          ? JSON.stringify(initialFoldersJson)
          : '[]'
    setFolders(parseListingsFoldersJson(raw))
    setProcessedFolderId(initialProcessedFolderId ?? '')
  }, [initialFoldersJson, initialProcessedFolderId])

  const saveMutation = useMutation({
    mutationFn: async () => {
      const now = new Date().toISOString()
      const rows = [
        {
          key: APP_SETTINGS_N8N_LISTINGS_FOLDERS_KEY,
          value: serializeListingsFolders(folders),
          updated_at: now,
        },
        {
          key: APP_SETTINGS_N8N_PROCESSED_FOLDER_KEY,
          value: processedFolderId.trim(),
          updated_at: now,
        },
      ]
      const { error } = await supabase.from('app_settings').upsert(rows, { onConflict: 'key' })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_SETTINGS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: LISTINGS_MANAGEMENT_DASHBOARD_QUERY_KEY })
      setMessage({ type: 'success', text: 'Folder configuration saved.' })
      setTimeout(() => setMessage(null), 5000)
    },
    onError: (err: Error) => {
      setMessage({ type: 'error', text: err.message })
      setTimeout(() => setMessage(null), 8000)
    },
  })

  const updateFolder = (index: number, patch: Partial<ListingsFolderEntry>) => {
    setFolders((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)))
  }

  return (
    <AdminSectionCard id="folders" title="Google Drive folder configuration">
      <p className="mb-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
        Broadcasters drop accessibility and streaming CSV/XLSX files into the inbox folders below. After{' '}
        <strong>Refresh listings</strong> runs (via n8n), processed files move to the Processed folder. Parser and
        stream are inferred from the folder — no upload picker in the admin UI.
      </p>

      <label className="block mb-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
        Processed folder ID (Shared Drive)
        <input
          type="text"
          value={processedFolderId}
          onChange={(e) => setProcessedFolderId(e.target.value)}
          className="block mt-1 w-full max-w-xl min-h-[44px] px-3 rounded border-2 text-sm font-mono"
          style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', backgroundColor: 'var(--bg-primary)' }}
          placeholder="Google Drive folder ID"
        />
      </label>

      {folders.length === 0 ? (
        <p className="mb-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
          No inbox folders configured. Apply migration 108 or add entries below.
        </p>
      ) : (
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ borderBottom: '2px solid var(--bg-tertiary)' }}>
                <th className="text-left py-2 pr-3" style={{ color: 'var(--text-secondary)' }}>Label</th>
                <th className="text-left py-2 pr-3" style={{ color: 'var(--text-secondary)' }}>Stream</th>
                <th className="text-left py-2 pr-3" style={{ color: 'var(--text-secondary)' }}>Type</th>
                <th className="text-left py-2 pr-3" style={{ color: 'var(--text-secondary)' }}>Drive folder ID</th>
                <th className="text-left py-2" style={{ color: 'var(--text-secondary)' }}>Enabled</th>
              </tr>
            </thead>
            <tbody>
              {folders.map((folder, index) => (
                <tr key={folder.key} style={{ borderBottom: '1px solid var(--bg-tertiary)' }}>
                  <td className="py-2 pr-3" style={{ color: 'var(--text-primary)' }}>{folder.label}</td>
                  <td className="py-2 pr-3" style={{ color: 'var(--text-primary)' }}>{folder.stream}</td>
                  <td className="py-2 pr-3" style={{ color: 'var(--text-primary)' }}>{folder.ingest_type}</td>
                  <td className="py-2 pr-3">
                    <input
                      type="text"
                      value={folder.drive_folder_id}
                      onChange={(e) => updateFolder(index, { drive_folder_id: e.target.value })}
                      className="w-full min-w-[12rem] min-h-[36px] px-2 rounded border text-xs font-mono"
                      style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', backgroundColor: 'var(--bg-primary)' }}
                    />
                  </td>
                  <td className="py-2">
                    <input
                      type="checkbox"
                      checked={folder.enabled}
                      onChange={(e) => updateFolder(index, { enabled: e.target.checked })}
                      aria-label={`Enable ${folder.label}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button
        type="button"
        onClick={() => saveMutation.mutate()}
        disabled={saveMutation.isPending}
        className="px-4 py-2 font-semibold rounded text-sm"
        style={{ backgroundColor: 'var(--bg-interactive)', color: 'var(--text-inverse)' }}
      >
        {saveMutation.isPending ? 'Saving…' : 'Save folder configuration'}
      </button>

      {message && (
        <p
          className="mt-3 text-sm"
          role="alert"
          style={{ color: message.type === 'error' ? 'var(--color-error)' : 'var(--text-primary)' }}
        >
          {message.text}
        </p>
      )}
    </AdminSectionCard>
  )
}
